import { useEffect, useMemo, useRef, useState } from 'react'
import StatusPill from '../StatusPill'
import { statusTone } from '../statusMaps'
import WindowChip from '../WindowChip'
import SlaChip from '../SlaChip'
import FilaFiltros from './FilaFiltros'
import {
  CLASES_TONO, completoPorIA, estadoAtencion, reabiertaSinAtender, requiereRevisionCritica,
} from '../../atencion'
import {
  DIAS_RESUELTAS, esHistorica, filasVisibles, filtroMuestraLaVista, vistaDe, type InboxFilter,
} from '../../bandeja'
import { useMe } from '../../auth/useMe'
import { useAuth } from '../../auth/useAuth'
import type { ConversationSummary } from '../../types/portal'
import { telefonoVisible } from '../../identidad'
import {
  horaDeLaFila, noLeidos, ordenDeLaLista, type Vistos, type VistosDelServidor,
} from '../../noLeidos'
import { useNoLeidos } from '../../useNoLeidos'
import InvitacionAvisos from '../InvitacionAvisos'

/**
 * Columna 1 de la bandeja — "Mis conversaciones", 292 px.
 *
 * Buscador + tres filtros, la lista, y el pie con la posición en la rotación.
 * Cada ítem muestra el chip de la ventana de 24 h para que el asesor priorice
 * SIN abrir cada chat: si una está por vencer, es la que hay que atender ya.
 *
 * Qué filas se ven lo decide `src/bandeja.ts` (vistas, filtros y buscador),
 * que se prueba en Node; acá queda la presentación.
 */

export type { InboxFilter }

/**
 * Los filtros cambian según el modo. En la bandeja del asesor todo lo que ve ya
 * es suyo, así que "Pendientes" (estado ≠ ACTIVA) es lo útil. En monitoreo ve
 * las de todo el equipo, y lo que importa es qué falta repartir.
 */
const FILTROS_BANDEJA: Array<{ key: InboxFilter; label: string }> = [
  { key: 'todas', label: 'En curso' },
  { key: 'pendientes', label: 'Pendientes' },
  { key: 'documentos', label: 'Documentos' },
  { key: 'completos', label: 'Completos' },
  { key: 'historicas', label: 'Históricas' },
  // DEF-047: lo que uno resolvió no desaparece. Solo en la bandeja: En vivo no
  // recibe resueltas.
  { key: 'resueltas', label: 'Resueltas' },
]

const FILTROS_MONITOR: Array<{ key: InboxFilter; label: string }> = [
  { key: 'todas', label: 'En curso' },
  { key: 'sin_asignar', label: 'Sin asignar' },
  { key: 'documentos', label: 'Documentos' },
  { key: 'completos', label: 'Completos' },
  { key: 'historicas', label: 'Históricas' },
]

/** Los filtros que solo aparecen si tienen algo, o si están elegidos: un
 *  filtro vacío es ruido, y uno elegido que desaparece deja al usuario sin
 *  cómo salir. */
const OCASIONALES = new Set<InboxFilter>(['completos', 'historicas', 'resueltas'])

/** Lo que dice cada filtro ocasional al pasar el mouse. */
const TITULO_FILTRO: Partial<Record<InboxFilter, string>> = {
  completos: 'Completas por el agente IA: recibió y validó el checklist sin alertas.',
  historicas: 'Sin actividad hace más de 24 h, o ya en el precalificador del socio.',
  resueltas: `Las que resolviste en los últimos ${DIAS_RESUELTAS} días. Si el cliente `
    + 'vuelve a escribir, se reabre y sale de acá.',
}

/** Color del borde izquierdo del ítem: el mismo tono que su pill de estado. */
const BORDE_POR_TONO: Record<string, string> = {
  green:  'border-l-brand',
  amber:  'border-l-warn',
  red:    'border-l-danger',
  blue:   'border-l-info',
  purple: 'border-l-purple',
  gray:   'border-l-surface-border',
}

/** Una sola referencia: un `[]` nuevo en cada render dispararía los efectos. */
const SIN_RESUELTAS: ConversationSummary[] = []

function iniciales(nombre: string, telefono: string): string {
  const base = nombre.trim() || telefono
  const partes = base.split(/\s+/).filter(Boolean)
  if (partes.length >= 2) return (partes[0][0] + partes[1][0]).toUpperCase()
  return base.slice(0, 2).toUpperCase()
}

export default function ConversationList({
  conversations, resueltas = SIN_RESUELTAS, selectedId, onSelect, loading, error,
  variant = 'inbox', myEmail = '', vistosDelServidor = null,
}: {
  conversations: ConversationSummary[]
  /** Las que resolvió quien mira y siguen cerradas (DEF-047). Solo la bandeja. */
  resueltas?: ConversationSummary[]
  selectedId: string | null
  onSelect: (id: string) => void
  loading: boolean
  /** Falló la carga: la lista dice eso en vez de "no tienes conversaciones",
   *  que haría pensar que la bandeja está vacía cuando en realidad se cayó. */
  error: string | null
  /** `monitor` = En vivo: todas las del equipo, con el asesor que atiende. */
  variant?: 'inbox' | 'monitor'
  /** Para marcar en la lista las que son del propio usuario. */
  myEmail?: string
  /** Lo visto por quien mira, como llegó con la lista (`delServidor`). */
  vistosDelServidor?: VistosDelServidor | null
}) {
  const esMonitor = variant === 'monitor'
  const FILTROS = esMonitor ? FILTROS_MONITOR : FILTROS_BANDEJA
  const [filtro, setFiltro] = useState<InboxFilter>('todas')
  const [busqueda, setBusqueda] = useState('')
  const { available, rotationPosition } = useMe()
  // Quien no está en el grupo Asesor no recibe por round-robin: tiene bandeja
  // porque intervino o se asignó una. Decirle «Round-robin activo» es falso.
  const { atiende, user } = useAuth()
  // El número verde de cada fila: mensajes del cliente que quien mira no vio.
  // Con las resueltas también: si no entran en lo visto, la primera vez que se
  // abre ese filtro cada una aparece con todos sus mensajes como nuevos.
  const todas = useMemo(() => [...conversations, ...resueltas], [conversations, resueltas])
  const vistos = useNoLeidos(todas, selectedId, user?.email ?? myEmail, vistosDelServidor)

  const historicas = useMemo(
    () => conversations.filter(esHistorica).length,
    [conversations],
  )
  // Vivas y completas por el agente: salen de «En curso» porque nadie las está
  // atendiendo, y ahí se leían como trabajo en marcha.
  const completos = useMemo(
    () => conversations.filter((c) => !esHistorica(c) && completoPorIA(c)).length,
    [conversations],
  )
  const cuantas: Partial<Record<InboxFilter, number>> = {
    completos, historicas, resueltas: resueltas.length,
  }

  // LA ABIERTA SIEMPRE SE VE EN LA LISTA
  //
  // Si se abre una conversación que el filtro actual esconde —la que acabas de
  // tomar desde En vivo, completa por el agente—, el filtro pasa al que la
  // contiene. Solo cuando cambia la selección: si el usuario elige otro filtro
  // a propósito, no se lo desarma.
  const selPrevia = useRef<string | null>(null)
  useEffect(() => {
    if (!selectedId || selectedId === selPrevia.current) return
    const asignada = conversations.find((x) => x.conversation_id === selectedId)
    const resuelta = asignada ? undefined : resueltas.find((x) => x.conversation_id === selectedId)
    const c = asignada ?? resuelta
    if (!c) return
    selPrevia.current = selectedId
    const suyo: InboxFilter = vistaDe(c, Boolean(resuelta))
    if (!filtroMuestraLaVista(filtro, suyo)) setFiltro(suyo)
  }, [selectedId, conversations, resueltas, filtro])

  // Quien escribió último va arriba, como en WhatsApp (`noLeidos.ts`), y por
  // encima de todo las que requieren revisión por una alerta crítica
  // (DEF-039): un bloqueo o un embargo no puede quedar debajo de veinte
  // conversaciones que solo tienen un mensaje más nuevo. `filasVisibles`
  // filtra sin reordenar, así que el orden sobrevive.
  const ordenadas = useMemo(() => ordenDeLaLista(conversations), [conversations])
  const visibles = useMemo(
    () => filasVisibles({ conversaciones: ordenadas, resueltas, filtro, busqueda, esMonitor }),
    [ordenadas, resueltas, filtro, busqueda, esMonitor],
  )
  const buscando = busqueda.trim() !== ''

  return (
    <div className="flex min-h-0 w-[248px] shrink-0 flex-col border-r border-surface-border bg-white xl:w-[292px]">
      {/* Cabecera fija */}
      <div className="shrink-0 border-b border-surface-border px-3.5 py-3">
        <input
          className="input-sunken"
          placeholder={esMonitor
            ? 'Buscar cliente, teléfono o asesor…'
            : 'Buscar en mis conversaciones…'}
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
        />
        <FilaFiltros>
          {FILTROS.filter((f) => !OCASIONALES.has(f.key) || (cuantas[f.key] ?? 0) > 0
                                 || filtro === f.key).map((f) => (
            <button
              key={f.key}
              onClick={() => setFiltro(f.key)}
              title={TITULO_FILTRO[f.key]}
              className={filtro === f.key && !buscando ? 'pill-filter-active' : 'pill-filter'}
            >
              {f.label}
              {OCASIONALES.has(f.key) && (cuantas[f.key] ?? 0) > 0 && (
                <span className="ml-1 font-mono opacity-80">{cuantas[f.key]}</span>
              )}
            </button>
          ))}
        </FilaFiltros>
        {/* Con texto, la búsqueda recorre todas las vistas (`bandeja.ts`): el
            filtro elegido deja de recortar, y se dice para que no parezca un
            filtro roto. */}
        {buscando && (
          <p className="mt-1.5 text-meta text-ink-faint">
            {esMonitor ? 'Buscando en todas las del equipo' : 'Buscando en toda tu bandeja'}
            {!esMonitor && resueltas.length > 0 && ', también en las resueltas'}
          </p>
        )}
      </div>

      {/* La franja de WhatsApp Web: «las notificaciones están desactivadas». */}
      <InvitacionAvisos />

      {/* Lista */}
      <div className="col-scroll scrollbar-thin flex-1">
        {loading ? (
          <ListaSkeleton />
        ) : error ? (
          <p className="px-3.5 py-8 text-center text-body text-danger">
            No se pudo cargar la lista.
          </p>
        ) : visibles.length === 0 ? (
          <p className="px-3.5 py-8 text-center text-body text-ink-faint">
            {buscando
              ? 'Ninguna conversación coincide con la búsqueda.'
              : filtro === 'resueltas'
              ? `No resolviste ninguna en los últimos ${DIAS_RESUELTAS} días, o el cliente las volvió a abrir.`
              : filtro !== 'historicas' && filtro !== 'completos'
              && historicas + completos + resueltas.length > 0
              && historicas + completos === conversations.length
              ? nadaEnCurso(completos, historicas, resueltas.length)
              : conversations.length > 0
              ? 'Ninguna conversación coincide con el filtro.'
              : esMonitor
                ? 'El equipo no tiene conversaciones abiertas ahora mismo.'
                : !atiende
                  ? 'No tienes conversaciones asignadas. Las que tomes desde En vivo o la cola aparecen acá.'
                : available
                  ? 'No tienes conversaciones asignadas. El round-robin te asignará la próxima que entre.'
                  : 'Estás en pausa: no recibirás conversaciones nuevas hasta que vuelvas a marcarte disponible.'}
          </p>
        ) : (
          visibles.map((c) => (
            <Item
              key={c.conversation_id}
              conv={c}
              selected={c.conversation_id === selectedId}
              onClick={() => onSelect(c.conversation_id)}
              mostrarAsesor={esMonitor}
              myEmail={myEmail}
              vistos={vistos}
            />
          ))
        )}
      </div>

      {/* Pie fijo */}
      <div className="flex shrink-0 items-center gap-2 border-t border-surface-faint bg-surface px-3.5 py-2.5">
        {esMonitor ? (
          <>
            <span className="h-[7px] w-[7px] shrink-0 rounded-full bg-info" />
            <p className="text-micro text-ink-muted">
              Monitoreo · {conversations.length - historicas - completos} en curso
              {completos > 0 && ` · ${completos} completa${completos === 1 ? '' : 's'}`}
              {historicas > 0 && ` · ${historicas} histórica${historicas === 1 ? '' : 's'}`}
            </p>
          </>
        ) : (
          !atiende ? (
            <>
              <span className="h-[7px] w-[7px] shrink-0 rounded-full bg-ink-ghost" />
              <p className="text-micro text-ink-muted">
                Fuera de la rotación · solo las que tomaste
              </p>
            </>
          ) : <>
            <span
              className={`h-[7px] w-[7px] shrink-0 rounded-full ${available ? 'bg-brand' : 'bg-warn'}`}
            />
            <p className="text-micro text-ink-muted">
              {available
                ? rotationPosition
                  ? <>Round-robin activo · eres el <b className="text-ink">{rotationPosition}º</b> en recibir</>
                  : 'Round-robin activo'
                : 'En pausa · fuera de la rotación'}
            </p>
          </>
        )}
      </div>
    </div>
  )
}

function AsesorPill({ email, myEmail }: { email: string | null; myEmail: string }) {
  // Tres estados con color propio: sin asignar es lo que el supervisor tiene
  // que repartir, así que va en violeta y no en gris — no se pierde de vista.
  if (!email) {
    return (
      <span className="inline-flex items-center rounded-chip bg-purple-light px-2 py-0.5 text-pill font-bold text-purple">
        Sin asignar
      </span>
    )
  }
  const esMia = email.toLowerCase() === myEmail.toLowerCase()
  return (
    <span
      title={email}
      className={`inline-flex items-center rounded-chip px-2 py-0.5 text-pill font-bold ${
        esMia ? 'bg-brand-light text-brand-hover' : 'bg-surface-subtle text-ink-muted'
      }`}
    >
      {esMia ? 'Tuya' : email.split('@')[0]}
    </span>
  )
}

function Item({ conv, selected, onClick, mostrarAsesor = false, myEmail = '', vistos }: {
  conv: ConversationSummary; selected: boolean; onClick: () => void
  mostrarAsesor?: boolean; myEmail?: string
  vistos: Vistos | null
}) {
  const completo = completoPorIA(conv)
  const resuelta = conv.status === 'CLOSED'
  const tono = completo ? 'green' : statusTone(conv.status, 'conversation')
  const docs = conv.docs_pending_review
  // La abierta no lleva número aunque la pestaña esté de fondo: se está
  // atendiendo, y el número de la pestaña ya avisa.
  const sinLeer = selected ? 0 : noLeidos(conv, vistos)

  return (
    <button
      onClick={onClick}
      className={`w-full border-b border-surface-subtle border-l-[3px] px-3.5 py-3 text-left transition-colors ${
        selected
          ? `${BORDE_POR_TONO[tono]} bg-brand-selected`
          : 'border-l-transparent hover:bg-surface'
      }`}
    >
      <div className="flex items-start gap-2.5">
        <div className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full bg-brand-light text-[12px] font-bold text-brand-hover">
          {iniciales(conv.contact_name, conv.phone)}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-2">
            <span className="truncate text-chat font-bold text-ink">
              {conv.contact_name || 'Sin nombre'}
            </span>
            {/* La hora del último mensaje del CLIENTE, que es la que ordena:
                con la de la última actividad, el recordatorio del bot ponía
                una hora que no coincidía con el orden. Verde y en negrita con
                mensajes sin ver, como en WhatsApp. */}
            <span className={`shrink-0 text-meta ${sinLeer > 0 ? 'font-bold text-brand' : 'text-ink-faint'}`}>
              {horaDeLaFila(conv.last_inbound_ts ?? conv.last_message_ts)}
            </span>
          </div>
          <p className="font-mono text-meta text-ink-faint">{telefonoVisible(conv)}</p>
          <div className="mt-1 flex items-center gap-2">
            <p className={`min-w-0 flex-1 truncate text-body ${sinLeer > 0 ? 'font-semibold text-ink' : 'text-ink-muted'}`}>
              {conv.last_message || 'Sin mensajes'}
            </p>
            {sinLeer > 0 && <BadgeSinLeer cuantos={sinLeer} />}
          </div>
          <div className="mt-1.5 flex flex-wrap gap-1">
            {/* Primero: un embargo o un bloqueo no puede verse igual que una
                hipoteca desde la lista (DEF-039). */}
            {/* Con el expediente ya en revisión, el chip de estado lo dice
                —«Requiere revisión: alerta crítica»— y éste lo repetiría. */}
            {!requiereRevisionCritica(conv) && conv.alerta_critica && <ChipAlertaCritica />}
            {/* DEF-047: volvió porque el cliente escribió sobre una resuelta, y
                todavía no la atendió una persona (`reabiertaSinAtender`). */}
            {reabiertaSinAtender(conv) && <ChipReabierta />}
            {/* Pasó por todos los asesores sin respuesta: no rota más y la
                mueve supervisión (documento de QA del 20-oct-2026). */}
            {conv.para_revisar && <ChipParaRevisar motivo={conv.para_revisar.reason} />}
            {/* Completo por IA dice en un chip lo que antes eran dos que se
                contradecían: «En revisión» + «Agente IA». */}
            {completo
              ? <ChipCompletoPorIA />
              : resuelta
              ? <ChipResuelta conv={conv} />
              : <StatusPill status={conv.status} expediente={conv.expediente ?? null} />}
            {mostrarAsesor && <AsesorPill email={conv.assigned_to} myEmail={myEmail} />}
            <WindowChip window={conv.window} sesionSocio={conv.sesion_socio} compact />
            {/* Cuánto lleva el cliente esperando a una persona. No se pinta si
                nadie espera, que es el caso normal. */}
            <SlaChip sla={conv.sla} atendida={conv.atendida}
                     paraRevisar={Boolean(conv.para_revisar)} compact />
            {!completo && <ChipQuienAtiende conv={conv} />}
            {docs > 0 && (
              <span className="inline-flex items-center rounded-chip bg-purple-light px-2 py-0.5 text-pill font-bold text-purple">
                {docs} doc{docs === 1 ? '' : 's'} por revisar
              </span>
            )}
          </div>
        </div>
      </div>
    </button>
  )
}

/**
 * Cuántos mensajes del cliente no viste: el círculo verde de WhatsApp.
 *
 * Verde vivo y no rojo: rojo en esta lista ya significa alerta crítica y SLA
 * vencido, y un mensaje sin leer no es una falla.
 */
function BadgeSinLeer({ cuantos }: { cuantos: number }) {
  return (
    <span
      title={`${cuantos} mensaje${cuantos === 1 ? '' : 's'} del cliente que no viste`}
      aria-label={`${cuantos} sin leer`}
      className="flex h-[19px] min-w-[19px] shrink-0 items-center justify-center rounded-full bg-brand px-1.5 text-[11px] font-bold leading-none text-white"
    >
      {cuantos > 99 ? '99+' : cuantos}
    </span>
  )
}

/**
 * «Alerta crítica» (DEF-039): el expediente vigente tiene un bloqueo, un
 * embargo o una restricción. La especificación la distingue de cualquier
 * otra alerta, y quien revisa veinte casos tiene que verla sin abrirlos.
 */
function ChipAlertaCritica() {
  return (
    <span
      title="El expediente tiene una alerta crítica (bloqueo, embargo o restricción). Ver «Qué revisar»."
      className="inline-flex items-center gap-1 rounded-chip border border-danger-border bg-danger-light px-2 py-0.5 text-pill font-bold text-danger"
    >
      <span className="h-[6px] w-[6px] rounded-full bg-danger" />
      Alerta crítica
    </span>
  )
}

/**
 * Quién atiende, en el ítem de la lista.
 *
 * Solo se pinta cuando hay algo que decidir: si ya la atiende un asesor, el
 * dato redundante es la pill del asesor que está al lado. Lo que el supervisor
 * necesita distinguir de un vistazo es dónde sigue hablando el bot y dónde no
 * hay nadie.
 */
function ChipQuienAtiende({ conv }: { conv: ConversationSummary }) {
  const e = estadoAtencion(conv)
  if (e.quien === 'asesor' || e.quien === 'cerrada') return null
  const texto = e.quien === 'ia' ? 'Agente IA'
    : e.quien === 'nadie' ? 'Sin atender'
    : 'Decidiendo'
  return (
    <span
      title={e.detalle}
      className={`inline-flex items-center rounded-chip border px-2 py-0.5 text-pill font-bold ${CLASES_TONO[e.tono]}`}
    >
      {texto}
    </span>
  )
}

function nadaEnCurso(completos: number, historicas: number, resueltas = 0): string {
  const partes = ['Nada en curso.']
  if (completos > 0) {
    partes.push(`${completos} completa${completos === 1 ? '' : 's'} por el agente IA en "Completos".`)
  }
  if (historicas > 0) {
    partes.push(`${historicas} sin actividad hace más de 24 h en "Históricas".`)
  }
  if (resueltas > 0) {
    partes.push(`${resueltas} que resolviste en "Resueltas".`)
  }
  return partes.join(' ')
}

/**
 * «Reabierta» (DEF-047): el cliente volvió a escribir sobre una conversación
 * resuelta y la conversación volvió a una bandeja. Se apaga cuando una persona
 * la atiende (`reabiertaSinAtender`).
 */
function ChipReabierta() {
  return (
    <span
      title="El cliente volvió a escribir en una conversación resuelta y se reabrió. Todavía no la atendió una persona."
      className="inline-flex items-center gap-1 rounded-chip border border-warn-border bg-warn-light px-2 py-0.5 text-pill font-bold text-warn"
    >
      <span className="h-[6px] w-[6px] rounded-full bg-warn-dot" />
      Reabierta
    </span>
  )
}

/** «Para revisar»: ningún asesor le respondió y el reloj ya no la mueve. */
function ChipParaRevisar({ motivo }: { motivo: string }) {
  return (
    <span
      title={`Para revisar${motivo ? `: ${motivo}` : ''}. Queda con su asesor hasta que un supervisor la reasigne.`}
      className="inline-flex items-center gap-1 rounded-chip border border-danger-border bg-danger-light px-2 py-0.5 text-pill font-bold text-danger"
    >
      <span className="h-[6px] w-[6px] rounded-full bg-danger" />
      Para revisar
    </span>
  )
}

/** «Resuelta»: la cerró quien mira y el cliente no volvió a escribir. */
function ChipResuelta({ conv }: { conv: ConversationSummary }) {
  const cuando = conv.resolved_at
    ? new Date(conv.resolved_at).toLocaleDateString('es-PE', { day: '2-digit', month: 'short' })
    : ''
  return (
    <span
      title="La resolviste. Si el cliente vuelve a escribir, se reabre y vuelve a tu bandeja si sigues en la rotación."
      className="inline-flex items-center rounded-chip border border-surface-border bg-surface px-2 py-0.5 text-pill font-bold text-ink-muted"
    >
      Resuelta{cuando && ` · ${cuando}`}
    </span>
  )
}

/** También lo usa el panel del expediente (03-nov-2026): el mismo chip en Mi
 *  bandeja, Mis expedientes y Operación › Expedientes. */
export function ChipCompletoPorIA() {
  return (
    <span
      title={'Documentación inicial recibida (§9.30): el agente IA recibió y validó el checklist '
        + 'sin alertas y el cliente no pidió un asesor. Falta la revisión del equipo.'}
      className="inline-flex items-center gap-1 rounded-chip border border-brand-border bg-brand-light px-2 py-0.5 text-pill font-bold text-brand-hover"
    >
      <svg width="9" height="9" viewBox="0 0 10 10" aria-hidden>
        <path d="M2 5.2 4.1 7.3 8 3" fill="none" stroke="currentColor" strokeWidth="1.6"
              strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      Completo por IA
    </span>
  )
}

function ListaSkeleton() {
  return (
    <div aria-busy>
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="flex gap-2.5 border-b border-surface-subtle px-3.5 py-3">
          <div className="h-[34px] w-[34px] shrink-0 animate-pulse rounded-full bg-surface-subtle" />
          <div className="flex-1 space-y-1.5">
            <div className="h-3 w-2/3 animate-pulse rounded bg-surface-subtle" />
            <div className="h-2.5 w-1/2 animate-pulse rounded bg-surface-subtle" />
            <div className="h-2.5 w-full animate-pulse rounded bg-surface-subtle" />
          </div>
        </div>
      ))}
    </div>
  )
}
